import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, InputText, FloatingField } from '../../components/aip';

// スタイルをインポート
import '../../styles/aip/index.css';

const API_BASE_URL = '/api/auth';

interface FieldErrors {
  email?: string;
}

/**
 * 仮登録ページコンポーネント
 * メールアドレスを入力して仮登録を行う
 */
export const RegisterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // クエリパラメータ取得
  const lp = searchParams.get('lp') || '';
  const ag = searchParams.get('ag') || '';

  /**
   * フォームのバリデーション
   */
  const validateForm = (): boolean => {
    const errors: FieldErrors = {};

    if (!email.trim()) {
      errors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = '有効なメールアドレスを入力してください';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length > 0;
  };

  /**
   * フィールドのエラーをクリア
   */
  const clearFieldError = (field: keyof FieldErrors) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          lp: lp || undefined,
          ag: ag || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || '登録に失敗しました');
      }

      setIsSuccess(true);
      setSuccessMessage(data.message || 'メールを送信しました。メールに記載のURLから本登録を行ってください。');
    } catch (err) {
      setError(err instanceof Error ? err.message : '登録に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // 成功時の表示
  if (isSuccess) {
    return (
      <div className="aip-html-body">
        <div className="aip-main" data-size="xs">
          <header className="aip-main__header">
            <div className="aip-main__header-container">
              <h1 className="aip-main__header-title">AiiPo Management</h1>
            </div>
          </header>

          <div className="aip-main__content">
            <div
              style={{
                textAlign: 'center',
                padding: '2rem 0',
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-green50, #e8f5e9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                }}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--color-success, #27ae60)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22,4 12,14.01 9,11.01" />
                </svg>
              </div>

              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  marginBottom: '1rem',
                  color: 'var(--color-text-primary, #333)',
                }}
              >
                確認メールを送信しました
              </h2>

              <p
                style={{
                  fontSize: '0.9375rem',
                  color: 'var(--color-text-secondary, #666)',
                  lineHeight: 1.6,
                  marginBottom: '2rem',
                }}
              >
                {successMessage}
              </p>

              <div
                style={{
                  backgroundColor: 'var(--color-gray50, #f5f5f5)',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  marginBottom: '2rem',
                }}
              >
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary, #666)',
                    margin: 0,
                  }}
                >
                  メールが届かない場合は、迷惑メールフォルダをご確認ください。
                </p>
              </div>

              <Link
                to="/meeting/manage-new/login"
                className="aip-link"
                style={{ fontSize: '0.9375rem' }}
              >
                ログインページへ戻る
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aip-html-body">
      <div className="aip-main" data-size="xs">
        <header className="aip-main__header">
          <div className="aip-main__header-container">
            <h1 className="aip-main__header-title">AiiPo Management</h1>
          </div>
        </header>

        <div className="aip-main__content">
          <h2 style={{ fontSize: '1.5rem', fontWeight: 500, marginBottom: '1rem' }}>
            新規登録
          </h2>

          <p
            style={{
              fontSize: '0.9375rem',
              color: 'var(--color-text-secondary, #666)',
              marginBottom: '1.5rem',
              lineHeight: 1.6,
            }}
          >
            メールアドレスを入力してください。<br />
            確認メールをお送りします。
          </p>

          {error && (
            <div
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: 'var(--color-red50)',
                color: 'var(--color-alert)',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
                fontSize: '0.875rem',
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="aip-form-container__body">
              {/* メールアドレス */}
              <FloatingField
                label="メールアドレス"
                htmlFor="email"
                column="2"
                required
                errorText={fieldErrors.email}
                hasError={!!fieldErrors.email}
              >
                <InputText
                  id="email"
                  name="email"
                  type="email"
                  placeholder="example@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearFieldError('email');
                  }}
                  autoComplete="email"
                  className="aip-floating-field__control"
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                />
              </FloatingField>

              {/* 送信ボタン */}
              <div data-column="2" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <Button
                  buttonType="solid-fill"
                  size="md"
                  type="submit"
                  disabled={isLoading}
                  style={{ width: '100%' }}
                >
                  {isLoading ? '送信中...' : '確認メールを送信'}
                </Button>

                <div style={{ textAlign: 'center' }}>
                  <Link to="/meeting/manage-new/login" className="aip-link">
                    すでにアカウントをお持ちの方はこちら
                  </Link>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;






